import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Filter, Loader2, RefreshCw, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  feedbackApi,
  type ListChatFeedbackParams,
  type ListFeedbackParams,
} from '@/api/feedback';
import { escalationApi } from '@/api/escalation';
import { feedbackConfigApi } from '@/api/feedback-config';
import { integrationsApi } from '@/api/integrations';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import {
  CHAT_FEEDBACK_CHANNELS,
  FEEDBACK_STATUSES,
  type ChatFeedbackChannel,
  type ChatFeedbackRating,
  type ChatMessageFeedback,
  type EscalationConfigView,
  type FeedbackConfigView,
  type FeedbackRecord,
  type FeedbackStatus,
} from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { JsonView } from '@/components/data/JsonView';
import { DataField } from '@/components/data/DataField';
import { LoadMore } from '@/components/data/LoadMore';
import { formatDate } from '@/lib/utils';

const statusVariant: Record<FeedbackStatus, 'default' | 'success' | 'warning'> = {
  pending: 'default',
  satisfied: 'success',
  escalated: 'warning',
};

interface Filters {
  username: string;
  status: FeedbackStatus | '';
}

const EMPTY: Filters = { username: '', status: '' };

export function Feedback(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('feedback.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('feedback.subtitle')}</p>
      </header>
      <Tabs defaultValue="support">
        <TabsList>
          <TabsTrigger value="support">{t('feedback.tabs.support')}</TabsTrigger>
          <TabsTrigger value="widget">{t('feedback.tabs.widget')}</TabsTrigger>
        </TabsList>
        <TabsContent value="support">
          <SupportFeedbackTab />
        </TabsContent>
        <TabsContent value="widget">
          <WidgetFeedbackTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SupportFeedbackTab(): JSX.Element {
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<Filters>(EMPTY);
  const [applied, setApplied] = React.useState<Filters>(EMPTY);
  const [openId, setOpenId] = React.useState<string | null>(null);

  const queryParams = React.useMemo<ListFeedbackParams>(() => {
    const p: ListFeedbackParams = { limit: 50 };
    if (applied.username) p.username = applied.username;
    if (applied.status) p.status = applied.status;
    return p;
  }, [applied]);

  const query = useInfiniteQuery({
    queryKey: ['feedback', queryParams],
    queryFn: ({ pageParam }) =>
      feedbackApi.list({ ...queryParams, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });

  const items: FeedbackRecord[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  const dirty = JSON.stringify(filters) !== JSON.stringify(applied);

  return (
    <div className="space-y-6">
      <FeedbackHitlCard />
      <EscalationCard />

      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="gap-2"
        >
          <RefreshCw className={query.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {t('common.refresh')}
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Filter className="h-4 w-4" />
            {t('audit.filtersTitle')}
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('feedback.username')}</Label>
              <Input
                value={filters.username}
                onChange={(e) => setFilters({ ...filters, username: e.target.value })}
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('feedback.status')}</Label>
              <Select
                value={filters.status || '__any'}
                onValueChange={(v) =>
                  setFilters({ ...filters, status: v === '__any' ? '' : (v as FeedbackStatus) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">{t('common.any')}</SelectItem>
                  {FEEDBACK_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFilters(EMPTY);
                setApplied(EMPTY);
              }}
              disabled={!dirty && !applied.username && !applied.status}
            >
              {t('common.reset')}
            </Button>
            <Button size="sm" onClick={() => setApplied(filters)} disabled={!dirty}>
              {t('common.apply')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : query.error ? (
            <p className="p-6 text-sm text-destructive">{unwrapError(query.error)}</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">{t('feedback.empty')}</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('feedback.tableHeader.when')}</TableHead>
                    <TableHead>{t('feedback.tableHeader.user')}</TableHead>
                    <TableHead>{t('feedback.tableHeader.status')}</TableHead>
                    <TableHead>{t('feedback.tableHeader.language')}</TableHead>
                    <TableHead>{t('feedback.tableHeader.category')}</TableHead>
                    <TableHead>{t('feedback.tableHeader.shipment')}</TableHead>
                    <TableHead>{t('feedback.tableHeader.zohoTicket')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow
                      key={row.feedbackId}
                      className="cursor-pointer"
                      onClick={() => setOpenId(row.feedbackId)}
                    >
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDate(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-xs">{row.username}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {t('audit.bot')}: {row.botId.slice(0, 12)}…
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant[row.status]}>{row.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <code className="font-mono text-xs">{row.language}</code>
                      </TableCell>
                      <TableCell className="text-xs">{row.category ?? '—'}</TableCell>
                      <TableCell>
                        <code className="font-mono text-xs">{row.shipmentId ?? '—'}</code>
                      </TableCell>
                      <TableCell>
                        <code className="font-mono text-xs">{zohoTicketOf(row) ?? '—'}</code>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                hasNext={query.hasNextPage ?? false}
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              />
            </>
          )}
        </CardContent>
      </Card>

      <FeedbackDetailDialog
        feedbackId={openId}
        onClose={() => setOpenId(null)}
        onDeleted={() => {
          setOpenId(null);
          void query.refetch();
        }}
      />
    </div>
  );
}

function FeedbackHitlCard(): JSX.Element | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId, currentOrg } = useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const query = useQuery({
    queryKey: ['org', 'feedback-config', currentBotId],
    queryFn: () => feedbackConfigApi.get(currentBotId!),
    enabled: !!currentBotId,
  });

  const updateMutation = useMutation({
    mutationFn: (enabled: boolean) => feedbackConfigApi.update(currentBotId!, { enabled }),
    onSuccess: () => {
      toast.success(t('feedback.hitl.savedToast', { defaultValue: 'Feedback configuration saved' }));
      void queryClient.invalidateQueries({ queryKey: ['org', 'feedback-config', currentBotId] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId || query.isLoading || !query.data) return null;

  const config: FeedbackConfigView = query.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">
            {t('feedback.hitl.title', { defaultValue: 'Post-support feedback' })}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('feedback.hitl.subtitle', {
              defaultValue: 'When a /support answer is resolved, ask the customer whether it helped (with a follow-up escalate option below).',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.enabled ? 'success' : 'outline'} className="text-[10px]">
            {config.enabled
              ? t('feedback.hitl.on', { defaultValue: 'Enabled' })
              : t('feedback.hitl.off', { defaultValue: 'Disabled' })}
          </Badge>
          <Switch
            checked={config.enabled}
            disabled={!canManage || updateMutation.isPending}
            onCheckedChange={(enabled) => updateMutation.mutate(enabled)}
          />
        </div>
      </CardHeader>
    </Card>
  );
}

function EscalationCard(): JSX.Element | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId, currentOrg } = useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const feedbackConfigQuery = useQuery({
    queryKey: ['org', 'feedback-config', currentBotId],
    queryFn: () => feedbackConfigApi.get(currentBotId!),
    enabled: !!currentBotId,
  });

  const query = useQuery({
    queryKey: ['org', 'escalation', currentBotId],
    queryFn: () => escalationApi.get(currentBotId!),
    enabled: !!currentBotId,
  });

  const integrationsQuery = useQuery({
    queryKey: ['integrations', currentBotId],
    queryFn: () => integrationsApi.list(),
    enabled: !!currentBotId,
  });

  const updateMutation = useMutation({
    mutationFn: (patch: Parameters<typeof escalationApi.update>[1]) =>
      escalationApi.update(currentBotId!, patch),
    onSuccess: () => {
      toast.success(t('feedback.escalation.savedToast', { defaultValue: 'Escalation configuration saved' }));
      void queryClient.invalidateQueries({ queryKey: ['org', 'escalation', currentBotId] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId || query.isLoading || !query.data) return null;

  const config: EscalationConfigView = query.data;
  const zohoAvailable = integrationsQuery.data?.items.some(
    (i) => i.type === 'zoho' && i.enabled,
  ) ?? false;
  const emailAvailable = integrationsQuery.data?.items.some(
    (i) => i.type === 'email' && i.enabled,
  ) ?? false;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">
            {t('feedback.escalation.title', { defaultValue: 'Escalation' })}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('feedback.escalation.subtitle', {
              defaultValue: 'Choose whether — and how — an unresolved support answer escalates to a human.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.enabled ? 'success' : 'outline'} className="text-[10px]">
            {config.enabled
              ? t('feedback.escalation.on', { defaultValue: 'Enabled' })
              : t('feedback.escalation.off', { defaultValue: 'Disabled' })}
          </Badge>
          <Switch
            checked={config.enabled}
            disabled={!canManage || updateMutation.isPending}
            onCheckedChange={(enabled) => updateMutation.mutate({ enabled })}
          />
        </div>
      </CardHeader>
      {feedbackConfigQuery.data?.enabled === false ? (
        <CardContent className="border-t pt-4 text-xs text-muted-foreground">
          {t('feedback.escalation.feedbackDisabledHint', {
            defaultValue: 'Post-support feedback is disabled above — no feedback record is ever created, so this escalation config has no effect.',
          })}
        </CardContent>
      ) : null}
      <CardContent className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:gap-6">
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={config.channels.zoho}
            disabled={!canManage || !zohoAvailable || updateMutation.isPending}
            onCheckedChange={(checked) =>
              updateMutation.mutate({ channels: { zoho: checked === true } })
            }
          />
          <span>
            {t('feedback.escalation.channelZoho', { defaultValue: 'Zoho ticket' })}
            {!zohoAvailable ? (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {t('feedback.escalation.zohoUnavailable', { defaultValue: '(configure the Zoho integration first)' })}
              </span>
            ) : null}
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={config.channels.email}
            disabled={!canManage || !emailAvailable || updateMutation.isPending}
            onCheckedChange={(checked) =>
              updateMutation.mutate({ channels: { email: checked === true } })
            }
          />
          <span>
            {t('feedback.escalation.channelEmail', { defaultValue: 'Email' })}
            {!emailAvailable ? (
              <span className="ml-1.5 text-xs text-muted-foreground">
                {t('feedback.escalation.emailUnavailable', { defaultValue: '(configure the Email integration first)' })}
              </span>
            ) : null}
          </span>
        </label>
      </CardContent>
    </Card>
  );
}

interface ChatFilters {
  rating: ChatFeedbackRating | '';
  channel: ChatFeedbackChannel | '';
}

const CHAT_EMPTY: ChatFilters = { rating: '', channel: '' };

function WidgetFeedbackTab(): JSX.Element {
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<ChatFilters>(CHAT_EMPTY);

  const queryParams = React.useMemo<ListChatFeedbackParams>(() => {
    const p: ListChatFeedbackParams = { limit: 50 };
    if (filters.rating) p.rating = filters.rating;
    if (filters.channel) p.channel = filters.channel;
    return p;
  }, [filters]);

  const query = useInfiniteQuery({
    queryKey: ['chat-feedback', queryParams],
    queryFn: ({ pageParam }) =>
      feedbackApi.listChat({ ...queryParams, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });

  const items: ChatMessageFeedback[] = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="gap-2"
        >
          <RefreshCw className={query.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          {t('common.refresh')}
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Filter className="h-4 w-4" />
            {t('audit.filtersTitle')}
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{t('feedback.widget.rating')}</Label>
              <Select
                value={filters.rating || '__any'}
                onValueChange={(v) =>
                  setFilters({ ...filters, rating: v === '__any' ? '' : (v as ChatFeedbackRating) })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">{t('feedback.widget.anyRating')}</SelectItem>
                  <SelectItem value="up">👍 up</SelectItem>
                  <SelectItem value="down">👎 down</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t('feedback.widget.channel')}</Label>
              <Select
                value={filters.channel || '__any'}
                onValueChange={(v) =>
                  setFilters({
                    ...filters,
                    channel: v === '__any' ? '' : (v as ChatFeedbackChannel),
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any">{t('feedback.widget.anyChannel')}</SelectItem>
                  {CHAT_FEEDBACK_CHANNELS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{t('feedback.widget.subtitle')}</p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('common.loading')}
            </div>
          ) : query.error ? (
            <p className="p-6 text-sm text-destructive">{unwrapError(query.error)}</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">{t('feedback.widget.empty')}</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('feedback.widget.tableHeader.when')}</TableHead>
                    <TableHead>{t('feedback.widget.tableHeader.rating')}</TableHead>
                    <TableHead>{t('feedback.widget.tableHeader.comment')}</TableHead>
                    <TableHead>{t('feedback.widget.tableHeader.channel')}</TableHead>
                    <TableHead>{t('feedback.widget.tableHeader.session')}</TableHead>
                    <TableHead>{t('feedback.widget.tableHeader.message')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow key={`${row.sessionId}#${row.messageId}`}>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDate(Math.floor(row.createdAt / 1000))}
                      </TableCell>
                      <TableCell>
                        {row.rating === 'up' ? (
                          <ThumbsUp className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <ThumbsDown className="h-4 w-4 text-destructive" />
                        )}
                      </TableCell>
                      <TableCell className="max-w-[28ch] truncate text-xs" title={row.comment ?? ''}>
                        {row.comment ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.channel}</Badge>
                      </TableCell>
                      <TableCell>
                        <code className="font-mono text-[10px]">{row.sessionId.slice(0, 8)}…</code>
                      </TableCell>
                      <TableCell>
                        <code className="font-mono text-[10px]">{row.messageId.slice(0, 8)}…</code>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                hasNext={query.hasNextPage ?? false}
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FeedbackDetailDialog({
  feedbackId,
  onClose,
  onDeleted,
}: {
  feedbackId: string | null;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['feedback', 'detail', feedbackId],
    queryFn: () => feedbackApi.get(feedbackId!),
    enabled: feedbackId !== null,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => feedbackApi.delete(id),
    onSuccess: () => {
      toast.success(t('feedback.deletedToast'));
      void queryClient.invalidateQueries({ queryKey: ['feedback'] });
      onDeleted();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const r = detail.data;

  return (
    <Dialog open={feedbackId !== null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('feedback.detailTitle')}</DialogTitle>
          <DialogDescription>
            <code className="font-mono text-xs">{feedbackId}</code>
          </DialogDescription>
        </DialogHeader>
        {detail.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </div>
        ) : detail.error ? (
          <p className="text-sm text-destructive">{unwrapError(detail.error)}</p>
        ) : !r ? null : (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-3">
              <DataField label={t('feedback.fields.status')} value={<Badge variant={statusVariant[r.status]}>{r.status}</Badge>} />
              <DataField label={t('feedback.fields.created')} value={formatDate(r.createdAt)} />
              <DataField label={t('feedback.fields.expires')} value={formatDate(r.expiresAt)} />
              <DataField label={t('feedback.fields.username')} value={r.username} mono />
              <DataField label={t('feedback.fields.userId')} value={r.userId} mono />
              <DataField label={t('feedback.fields.bot')} value={r.botId} mono />
              <DataField label={t('feedback.fields.channel')} value={r.channel ?? '—'} mono />
              <DataField label={t('feedback.fields.language')} value={r.language} mono />
              <DataField label={t('feedback.fields.session')} value={r.sessionId} mono />
              <DataField label={t('feedback.fields.shipment')} value={r.shipmentId} mono />
              <DataField label={t('feedback.fields.contactEmail')} value={r.contactEmail} />
              <DataField label={t('feedback.fields.category')} value={r.category} />
              {zohoTicketOf(r) ? (
                <DataField label={t('feedback.fields.zohoTicket')} value={zohoTicketOf(r)} mono />
              ) : null}
              {r.satisfiedAt ? (
                <DataField label={t('feedback.fields.satisfiedAt')} value={formatDate(r.satisfiedAt)} />
              ) : null}
              {r.escalatedAt ? (
                <DataField label={t('feedback.fields.escalatedAt')} value={formatDate(r.escalatedAt)} />
              ) : null}
            </div>
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t('feedback.fields.originalPrompt')}
              </div>
              <JsonView value={r.originalPrompt} />
            </div>
            <div className="space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {t('feedback.fields.resolvedAnswer')}
              </div>
              <pre className="max-h-[40vh] overflow-auto rounded-md border bg-muted/30 p-3 text-xs whitespace-pre-wrap">
                {r.resolvedAnswer}
              </pre>
            </div>
            {r.escalationComment ? (
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {t('feedback.fields.escalationComment')}
                </div>
                <p className="rounded-md border bg-muted/30 p-3 text-xs italic">
                  {r.escalationComment}
                </p>
              </div>
            ) : null}
          </div>
        )}
        <DialogFooter className="sm:justify-between">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => feedbackId && deleteMutation.mutate(feedbackId)}
            disabled={deleteMutation.isPending}
            className="gap-1.5"
          >
            <Trash2 className="h-3 w-3" />
            {t('common.delete')}
          </Button>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function zohoTicketOf(record: FeedbackRecord): string | undefined {
  return record.escalatedChannels?.find((c) => c.channel === 'zoho' && c.ok)?.ref;
}
